 import React, { useState, useRef, useEffect } from 'react';
import { Upload, RotateCcw, RotateCw, Download, Maximize2, Minimize2, Check, X, Layers, Globe, Twitter, MessageSquare, Sliders } from 'lucide-react';
import { FaDiscord } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';


const ProfileCustomizer = () => {
  const [baseImage, setBaseImage] = useState(null);
  const [caps, setCaps] = useState([]); // Changed from single cap to array of caps
  const [backgrounds, setBackgrounds] = useState([]);
  const [finalImage, setFinalImage] = useState(null);
  const [highResFinalImage, setHighResFinalImage] = useState(null); // New state for high-res image
  const [selectedItem, setSelectedItem] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [showProcessed, setShowProcessed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [baseImageLoaded, setBaseImageLoaded] = useState(false);
  const [canvasRendering, setCanvasRendering] = useState(false);
  const [imageQuality, setImageQuality] = useState("standard"); // "standard" or "high"
  const [twitterUsername, setTwitterUsername] = useState('');
  const [isLoadingTwitterProfile, setIsLoadingTwitterProfile] = useState(false);
  const [twitterProfileError, setTwitterProfileError] = useState(null);
  
  // Add these new state variables
  const [activeTab, setActiveTab] = useState('stickers'); // 'stickers' or 'filters'
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterStrength, setFilterStrength] = useState(50); // Default 50% strength
  
  // Define available filters with their RGB values
  const colorFilters = [
    { id: 'pink', name: 'Pink', color: '#F06FBE', rgb: [240, 111, 190] },
    { id: 'blue', name: 'Blue', color: '#4DA8DA', rgb: [77, 168, 218] },
    { id: 'lime', name: 'Lime', color: '#B9F73E', rgb: [185, 247, 62] },
    { id: 'orange', name: 'Orange', color: '#FF9062', rgb: [255, 144, 98] },
    { id: 'purple', name: 'Purple', color: '#9747FF', rgb: [151, 71, 255] }
  ];
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
 
  // Add these states to handle layer dragging
  const [layerOrder, setLayerOrder] = useState([]);
  const [isDraggingLayer, setIsDraggingLayer] = useState(false);
  const [draggedLayerId, setDraggedLayerId] = useState(null);
  const [draggedOverLayerId, setDraggedOverLayerId] = useState(null);

  // Dynamically load all available items from folders
  useEffect(() => {
    const loadItems = async () => {
      setIsLoading(true);
     
      try {
        // In a real application, make an API call to get items from server
        // For development/testing, we'll try to use the API endpoint
        // and fall back to mock data if it's not available
        try {
          const response = await fetch('/api/items');
          if (response.ok) {
            const data = await response.json();
            setAllItems(data);
            return;
          }
        } catch (apiError) {
          console.warn('API not available, using mock data instead', apiError);
        }
       
        // If API call fails, use mock data as fallback
        console.log('Loading mock item data');
       
        // Get all image files from the public directory (this is a simplified approach)
        // In a real implementation with a proper backend, this would come from server
       
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 300));
       
        // Mock items (only used if API endpoint is not available)
        const mockItems = [
          // Caps - will use the actual images if they exist in your public folder
          { id: 'pink_cap', type: 'cap', src: '/images/caps/pink_cap.png', name: 'Pink Cap' },
          { id: 'teal_cap', type: 'cap', src: '/images/caps/teal_cap.png', name: 'Teal Cap' },
          { id: 'black_cap', type: 'cap', src: '/images/caps/black_cap.png', name: 'Black Cap' },
          { id: 'white_cap', type: 'cap', src: '/images/caps/white_cap.png', name: 'White Cap' },
          { id: 'red_cap', type: 'cap', src: '/images/caps/red_cap.png', name: 'Red Cap' },
          { id: 'blue_cap', type: 'cap', src: '/images/caps/blue_cap.png', name: 'Blue Cap' },
         
          // Backgrounds
          { id: 'sparkle', type: 'background', src: '/images/backgrounds/sparkle.png', name: 'Sparkle' },
          { id: 'star', type: 'background', src: '/images/backgrounds/star.png', name: 'Star' },
          { id: 'moon', type: 'background', src: '/images/backgrounds/moon.png', name: 'Moon' },
          { id: 'sun', type: 'background', src: '/images/backgrounds/sun.png', name: 'Sun' },
          { id: 'cloud', type: 'background', src: '/images/backgrounds/cloud.png', name: 'Cloud' },
          { id: 'rainbow', type: 'background', src: '/images/backgrounds/rainbow.png', name: 'Rainbow' },
        ];
       
        setAllItems(mockItems);
      } catch (error) {
        console.error('Failed to load items:', error);
      } finally {
        setIsLoading(false);
      }
    };
   
    loadItems();
  }, []);
 
  // Update useEffect to initialize layerOrder
  useEffect(() => {
    // Generate array of combined items IDs in appropriate order
    const laserItems = [...caps, ...backgrounds].filter(item => 
      item.isLaser || 
      item.src.toLowerCase().includes('laser') || 
      (item.name && item.name.toLowerCase().includes('laser'))
    ).map(item => item.id);
    
    const normalItems = [...caps, ...backgrounds].filter(item => 
      !item.isLaser && 
      !item.src.toLowerCase().includes('laser') && 
      !(item.name && item.name.toLowerCase().includes('laser'))
    ).map(item => item.id);
    
    // Laser items on top, then normal items
    setLayerOrder([...laserItems, ...normalItems]);
  }, [caps, backgrounds]);

  // Add a useEffect to ensure layerOrder includes newly added items
useEffect(() => {
  const allItemIds = [...caps, ...backgrounds].map(item => item.id);
  
  // Find any IDs in allItemIds that aren't in layerOrder
  const newIds = allItemIds.filter(id => !layerOrder.includes(id));
  
  if (newIds.length > 0) {
    // Update layerOrder to include new items
    // Separate laser items to ensure they stay on top
    const laserItems = newIds.filter(id => {
      const item = [...caps, ...backgrounds].find(item => item.id === id);
      return item && (
        item.isLaser || 
        item.src.toLowerCase().includes('laser') || 
        (item.name && item.name.toLowerCase().includes('laser'))
      );
    });
    
    const nonLaserItems = newIds.filter(id => !laserItems.includes(id));
    
    // Update layerOrder with laser items on top
    setLayerOrder(prev => {
      // Remove any ids that no longer exist
      const validOldIds = prev.filter(id => 
        caps.some(cap => cap.id === id) || backgrounds.some(bg => bg.id === id)
      );
      
      return [...laserItems, ...validOldIds, ...nonLaserItems];
    });
  } else if (layerOrder.length > allItemIds.length) {
    // Some items have been removed, clean up layerOrder
    setLayerOrder(prev => prev.filter(id => allItemIds.includes(id)));
  }
}, [caps, backgrounds]);

  // Handle canvas mouse/touch events for dragging
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
   
    // Get canvas scaling factor to handle high-DPI displays
    const getScalingFactor = () => {
      const rect = canvas.getBoundingClientRect();
      return {
        scaleX: canvas.width / rect.width,
        scaleY: canvas.height / rect.height
      };
    };
   
    // Convert client coordinates to canvas coordinates
    const clientToCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaling = getScalingFactor();
      return {
        x: (clientX - rect.left) * scaling.scaleX,
        y: (clientY - rect.top) * scaling.scaleY
      };
    };
   
    // Mouse events - with proper coordinate conversion
    const handleMouseDown = (e) => {
      if (!baseImage || canvasRendering) return;
     
      const coords = clientToCanvasCoords(e.clientX, e.clientY);
      handleDragStart(coords.x, coords.y);
    };
   
    const handleMouseMove = (e) => {
      if (!isDragging || !baseImage || canvasRendering) return;
     
      const coords = clientToCanvasCoords(e.clientX, e.clientY);
      handleDragMove(coords.x, coords.y);
    };
   
    const handleMouseUp = () => {
      setIsDragging(false);
    };
   
    // Touch events for mobile - with proper coordinate conversion
    const handleTouchStart = (e) => {
      if (!baseImage || !e.touches[0] || canvasRendering) return;
     
      const coords = clientToCanvasCoords(e.touches[0].clientX, e.touches[0].clientY);
      handleDragStart(coords.x, coords.y);
     
      // Prevent default to avoid scrolling
      if (isDragging) {
        e.preventDefault();
      }
    };
   
    const handleTouchMove = (e) => {
      if (!isDragging || !baseImage || !e.touches[0] || canvasRendering) return;
     
      const coords = clientToCanvasCoords(e.touches[0].clientX, e.touches[0].clientY);
      handleDragMove(coords.x, coords.y);
     
      // Prevent scrolling while dragging
      e.preventDefault();
    };
   
    const handleTouchEnd = () => {
      setIsDragging(false);
    };
   
    // Add event listeners
    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
   
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);
   
    // Cleanup
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
     
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, dragStart, selectedItem, baseImage, caps, backgrounds, canvasRendering]);
 
  // Determine which item is being clicked based on coordinates
  // Enhanced handleDragStart function with improved checkmark detection

const handleDragStart = (x, y) => {
  if (!baseImage) return;
 
  let itemFound = false;
  console.log("Click at:", x, y);
  
  // First check if we clicked on a checkmark button
  let checkmarkClicked = false;
  
  // Check cap checkmarks
  for (let i = caps.length - 1; i >= 0; i--) {
    const cap = caps[i];
    if (cap.checkmarkBounds) {
      const { x: btnX, y: btnY, width: btnWidth, height: btnHeight } = cap.checkmarkBounds;
      
      // Check if click is within the circular checkmark button
      const centerX = btnX + btnWidth/2;
      const centerY = btnY + btnHeight/2;
      const radius = btnWidth/2;
      const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
      
      if (distance <= radius) {
        // Checkmark clicked - confirm the selection
        setSelectedItem(null); // Deselect to return to normal state
        checkmarkClicked = true;
        itemFound = true;
        break;
      }
    }
  }
  
  // If no cap checkmark was clicked, check background checkmarks
  if (!checkmarkClicked) {
    for (let i = backgrounds.length - 1; i >= 0; i--) {
      const bg = backgrounds[i];
      if (bg.checkmarkBounds) {
        const { x: btnX, y: btnY, width: btnWidth, height: btnHeight } = bg.checkmarkBounds;
        
        // Check if click is within the circular checkmark button
        const centerX = btnX + btnWidth/2;
        const centerY = btnY + btnHeight/2;
        const radius = btnWidth/2;
        const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        
        if (distance <= radius) {
          // Checkmark clicked - confirm the selection
          setSelectedItem(null); // Deselect to return to normal state
          checkmarkClicked = true;
          itemFound = true;
          break;
        }
      }
    }
  }
  
  // If not a checkmark click, check if we clicked directly on an item
  if (!checkmarkClicked) {
    // Check caps first (since they're drawn last, they should be checked first)
    for (let i = caps.length - 1; i >= 0; i--) {
      const cap = caps[i];
      if (x >= cap.x && x <= cap.x + cap.width &&
          y >= cap.y && y <= cap.y + cap.height) {
        setSelectedItem(cap.id);
        setIsDragging(true);
        setDragStart({ x, y });
        itemFound = true;
        break;
      }
    }
   
    // If no cap was found, check backgrounds
    if (!itemFound) {
      for (let i = backgrounds.length - 1; i >= 0; i--) {
        const bg = backgrounds[i];
        if (x >= bg.x && x <= bg.x + bg.width &&
            y >= bg.y && y <= bg.y + bg.height) {
          setSelectedItem(bg.id);
          setIsDragging(true);
          setDragStart({ x, y });
          itemFound = true;
          break;
        }
      }
    }
  }
 
  // If no item was found and not a checkmark click, deselect
  if (!itemFound) {
    setSelectedItem(null);
  }
};
 
  // Handle drag movement
  const handleDragMove = (x, y) => {
    if (!isDragging || !selectedItem) return;
   
    const dx = x - dragStart.x;
    const dy = y - dragStart.y;
   
    // Update cap position if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? { ...cap, x: cap.x + dx, y: cap.y + dy }
          : cap
      ));
    } else {
      // Otherwise, update background position
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? { ...bg, x: bg.x + dx, y: bg.y + dy }
          : bg
        )
      );
    }
   
    setDragStart({ x, y });
  };
 
  const handleBaseImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
   
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target || !e.target.result) return;
     
      const img = new Image();
     
      img.onload = () => {
        // Canvas size is fixed at 800x800
        const canvasSize = 800;
        
        // Original dimensions
        const originalWidth = img.width;
        const originalHeight = img.height;
        
        // Calculate scaled dimensions to fit within canvas
        let width, height, x, y;
        
        // If the image is square or nearly square (within 5% difference)
        const aspectRatioDifference = Math.abs(originalWidth / originalHeight - 1);
        const isNearlySquare = aspectRatioDifference < 0.05;
        
        if (isNearlySquare) {
          // Treat as square - fill the canvas
          width = canvasSize;
          height = canvasSize;
          x = 0;
          y = 0;
        } else if (originalWidth > originalHeight) {
          // Landscape image - fit to width and center vertically
          width = canvasSize;
          height = (originalHeight / originalWidth) * canvasSize;
          x = 0;
          y = (canvasSize - height) / 2;
        } else {
          // Portrait image - fit to height and center horizontally
          height = canvasSize;
          width = (originalWidth / originalHeight) * canvasSize;
          x = (canvasSize - width) / 2;
          y = 0;
        }
        
        const newBaseImage = {
          src: img.src,
          x: x,
          y: y,
          width: width,
          height: height,
          originalWidth: img.width,
          originalHeight: img.height,
          needsUpscaling: originalWidth < 400 || originalHeight < 400,
          fillCanvas: isNearlySquare,
          isLandscape: originalWidth > originalHeight,
          isPortrait: originalHeight > originalWidth
        };
       
        setBaseImage(newBaseImage);
        setBaseImageLoaded(true);
        
        // Reset filter when a new image is uploaded
        setSelectedFilter(null);
        setFilterStrength(50);
      };
     
      img.src = e.target.result.toString();
    };
   
    reader.readAsDataURL(file);
  };


  // Add a rotateItem function
  const rotateItem = (degrees) => {
    if (!selectedItem) return;
    
    // Make rotation more subtle (about 70% of original)
    const subtlerDegrees = degrees * 0.7;
    
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {
              ...cap,
              rotation: (cap.rotation || 0) + subtlerDegrees
            }
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {
              ...bg,
              rotation: (bg.rotation || 0) + subtlerDegrees
            }
          : bg
        )
      );
    }
  };
 
  // Update the addItem function to force a canvas redraw
const addItem = (item) => {
  if (!item || !item.src) return;
 
  const img = new Image();
 
  img.onload = () => {
    // Check if this is a laser item
    const isLaserItem = item.name?.toLowerCase().includes('laser') || 
                        item.src?.toLowerCase().includes('laser');
    
    if (item.type === 'cap') {
      const newCap = {
        id: `cap_${Date.now().toString()}`, // Unique ID for each cap
        src: img.src,
        name: item.name || '', // Store the name
        x: 50,
        y: 50,
        width: 100,
        height: 100,
        originalWidth: img.width,
        originalHeight: img.height,
        flippedX: false,
        flippedY: false,
        rotation: 0,
        isLaser: isLaserItem // Mark if this is a laser item
      };
     
      setCaps(prev => {
        const updatedCaps = [...prev, newCap];
        // Force a redraw after state update by using a timeout
        setTimeout(() => drawCanvas(), 0);
        return updatedCaps;
      });
      setSelectedItem(newCap.id);
    } else if (item.type === 'background') {
      const newBg = {
        id: `bg_${Date.now().toString()}`,
        src: img.src,
        name: item.name || '', // Store the name
        x: 100,
        y: 100,
        width: 150,
        height: 150,
        originalWidth: img.width,
        originalHeight: img.height,
        flippedX: false,
        flippedY: false,
        rotation: 0,
        isLaser: isLaserItem // Mark if this is a laser item
      };
      setBackgrounds(prev => {
        const updatedBackgrounds = [...prev, newBg];
        // Force a redraw after state update by using a timeout
        setTimeout(() => drawCanvas(), 0);
        return updatedBackgrounds;
      });
      setSelectedItem(newBg.id);
    }
  };
 
  img.onerror = () => {
    console.error(`Failed to load image: ${item.src}`);
  };
 
  img.src = item.src;
};
 
  const moveItem = (dx, dy) => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {...cap, x: cap.x + dx, y: cap.y + dy}
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {...bg, x: bg.x + dx, y: bg.y + dy}
          : bg
        )
      );
    }
  };
 
  const resizeItem = (factor) => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {
              ...cap,
              width: cap.width * factor,
              height: cap.height * factor
            }
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {
              ...bg,
              width: bg.width * factor,
              height: bg.height * factor
            }
          : bg
        )
      );
    }
  };
 
  const flipItem = (axis) => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {
              ...cap,
              flippedX: axis === 'x' ? !cap.flippedX : cap.flippedX,
              flippedY: axis === 'y' ? !cap.flippedY : cap.flippedY
            }
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {
              ...bg,
              flippedX: axis === 'x' ? !bg.flippedX : bg.flippedX,
              flippedY: axis === 'y' ? !bg.flippedY : bg.flippedY
            }
          : bg
        )
      );
    }
  };
 
  const removeItem = () => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.filter(cap => cap.id !== selectedItem));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev => prev.filter(bg => bg.id !== selectedItem));
    }
    setSelectedItem(null);
  };
 
  // Add these new functions to the component
  const stretchItemWidth = (factor) => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {
              ...cap,
              width: cap.width * factor
            }
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {
              ...bg,
              width: bg.width * factor
            }
          : bg
        )
      );
    }
  };


  const stretchItemHeight = (factor) => {
    if (!selectedItem) return;
   
    // Check if selected item is a cap
    const selectedCap = caps.find(cap => cap.id === selectedItem);
    if (selectedCap) {
      setCaps(prev => prev.map(cap =>
        cap.id === selectedItem
          ? {
              ...cap,
              height: cap.height * factor
            }
          : cap
      ));
    } else {
      // Otherwise it must be a background
      setBackgrounds(prev =>
        prev.map(bg => bg.id === selectedItem
          ? {
              ...bg,
              height: bg.height * factor
            }
          : bg
        )
      );
    }
  };
 
  // Update the processImage function to handle rotation
  const processImage = () => {
    if (!baseImage) return;
   
    // Create standard resolution version (400x400)
    createProcessedImage(400, 400, false);
    
    // Create high resolution version (1200x1200)
    createProcessedImage(1200, 1200, true);
  };
  
  // New function to create processed images at different resolutions
  const createProcessedImage = (width, height, isHighRes) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
   
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }
   
    // Only fill background if not a fill-canvas image
    if (!baseImage.fillCanvas) {
      ctx.fillStyle = '#F06FBE';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
   
    // Calculate scaling ratio based on the canvas size
    const canvasRatio = canvas.width / 800;
   
    // Load and draw the base image
    const baseImg = new Image();
    baseImg.onload = () => {
      // Scale the position and dimensions
      const scaledBaseX = baseImage.x * canvasRatio;
      const scaledBaseY = baseImage.y * canvasRatio;
      const scaledBaseWidth = baseImage.width * canvasRatio;
      const scaledBaseHeight = baseImage.height * canvasRatio;
     
      // Use better quality settings for the high-res version or Twitter images
      if (isHighRes || baseImage.needsUpscaling) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }
      
      // Fill with background color only if we're not filling the canvas
      if (!baseImage.fillCanvas) {
        ctx.fillStyle = '#F06FBE';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Draw the base image
      ctx.drawImage(
        baseImg,
        0, 0,
        baseImage.originalWidth, baseImage.originalHeight,
        scaledBaseX, scaledBaseY,
        scaledBaseWidth, scaledBaseHeight
      );
     
      // Optional: add subtle border for rectangular images in final output
      if ((baseImage.isLandscape || baseImage.isPortrait) && !baseImage.fillCanvas) {
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.lineWidth = 2 * canvasRatio;
        ctx.strokeRect(scaledBaseX, scaledBaseY, scaledBaseWidth, scaledBaseHeight);
      }
      
      // Apply color filter if one is selected
      if (selectedFilter) {
        const selectedFilterObj = colorFilters.find(filter => filter.id === selectedFilter);
        if (selectedFilterObj) {
          // Get the image data
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          // Apply the filter with the specified strength
          const strength = filterStrength / 100; // Convert percentage to decimal
          const [r, g, b] = selectedFilterObj.rgb;
          
          for (let i = 0; i < data.length; i += 4) {
            // Skip fully transparent pixels
            if (data[i + 3] === 0) continue;
            
            // Apply the filter based on strength
            data[i] = Math.round(data[i] * (1 - strength) + r * strength);
            data[i + 1] = Math.round(data[i + 1] * (1 - strength) + g * strength);
            data[i + 2] = Math.round(data[i + 2] * (1 - strength) + b * strength);
          }
          
          // Put the modified image data back
          ctx.putImageData(imageData, 0, 0);
        }
      }
     
      // Draw backgrounds and normal caps first
      const drawBackgrounds = () => {
        if (backgrounds.length === 0) {
          drawNormalCaps();
          return;
        }
        
        let loadedBgs = 0;
        backgrounds.forEach((bg, index) => {
          const bgImg = new Image();
          bgImg.onload = () => {
            ctx.save();
           
            // Scale position and dimensions
            const scaledX = bg.x * canvasRatio;
            const scaledY = bg.y * canvasRatio;
            const scaledWidth = bg.width * canvasRatio;
            const scaledHeight = bg.height * canvasRatio;
           
            // Calculate center point for transformations
            const centerX = scaledX + scaledWidth / 2;
            const centerY = scaledY + scaledHeight / 2;
           
            // Apply transformations
            ctx.translate(centerX, centerY);
            // Apply rotation if defined
            if (bg.rotation) {
              ctx.rotate((bg.rotation * Math.PI) / 180);
            }
            ctx.scale(bg.flippedX ? -1 : 1, bg.flippedY ? -1 : 1);
            ctx.translate(-centerX, -centerY);
           
            // Draw the image
            ctx.drawImage(
              bgImg,
              0, 0,
              bg.originalWidth, bg.originalHeight,
              scaledX, scaledY,
              scaledWidth, scaledHeight
            );
           
            ctx.restore();
           
            loadedBgs++;
            if (loadedBgs === backgrounds.length) {
              drawNormalCaps();
            }
          };
         
          bgImg.onerror = () => {
            console.error(`Failed to load background image: ${bg.src}`);
            loadedBgs++;
            if (loadedBgs === backgrounds.length) {
              drawNormalCaps();
            }
          };
         
          bgImg.src = bg.src;
        });
      };
     
      // Draw normal caps (non-laser)
      const drawNormalCaps = () => {
        const normalCaps = caps.filter(cap => !cap.isLaser && 
          !cap.src.toLowerCase().includes('laser') && 
          !(cap.name && cap.name.toLowerCase().includes('laser')));
        
        if (normalCaps.length === 0) {
          // If no normal caps, apply filter and then draw laser caps
          applyFilter();
          return;
        }
        
        let loadedCaps = 0;
        normalCaps.forEach((cap, index) => {
          const capImg = new Image();
          capImg.onload = () => {
            ctx.save();
           
            // Scale position and dimensions
            const scaledX = cap.x * canvasRatio;
            const scaledY = cap.y * canvasRatio;
            const scaledWidth = cap.width * canvasRatio;
            const scaledHeight = cap.height * canvasRatio;
           
            // Calculate center point for transformations
            const centerX = scaledX + scaledWidth / 2;
            const centerY = scaledY + scaledHeight / 2;
           
            // Apply transformations
            ctx.translate(centerX, centerY);
            // Apply rotation if defined
            if (cap.rotation) {
              ctx.rotate((cap.rotation * Math.PI) / 180);
            }
            ctx.scale(cap.flippedX ? -1 : 1, cap.flippedY ? -1 : 1);
            ctx.translate(-centerX, -centerY);
           
            // Draw the cap
            ctx.drawImage(
              capImg,
              0, 0,
              cap.originalWidth, cap.originalHeight,
              scaledX, scaledY,
              scaledWidth, scaledHeight
            );
           
            ctx.restore();
           
            loadedCaps++;
            if (loadedCaps === normalCaps.length) {
              applyFilter();
            }
          };
         
          capImg.onerror = () => {
            console.error(`Failed to load cap image: ${cap.src}`);
            loadedCaps++;
            if (loadedCaps === normalCaps.length) {
              applyFilter();
            }
          };
         
          capImg.src = cap.src;
        });
      };
     
      // Apply filter after normal caps but before laser caps
      const applyFilter = () => {
        // Apply color filter if selected
        if (selectedFilter) {
          const selectedFilterObj = colorFilters.find(filter => filter.id === selectedFilter);
          if (selectedFilterObj) {
            // Get the image data
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            
            // Apply the filter with the specified strength
            const strength = filterStrength / 100; // Convert percentage to decimal
            const [r, g, b] = selectedFilterObj.rgb;
            
            for (let i = 0; i < data.length; i += 4) {
              // Skip fully transparent pixels
              if (data[i + 3] === 0) continue;
              
              // Apply the filter based on strength
              data[i] = Math.round(data[i] * (1 - strength) + r * strength);
              data[i + 1] = Math.round(data[i + 1] * (1 - strength) + g * strength);
              data[i + 2] = Math.round(data[i + 2] * (1 - strength) + b * strength);
            }
            
            // Put the modified image data back
            ctx.putImageData(imageData, 0, 0);
          }
        }
        
        // After applying filter, draw laser caps
        drawLaserCaps();
      };
     
      // Draw laser caps on top of everything after the filter
      const drawLaserCaps = () => {
        const laserCaps = caps.filter(cap => cap.isLaser || 
          cap.src.toLowerCase().includes('laser') || 
          (cap.name && cap.name.toLowerCase().includes('laser')));
        
        if (laserCaps.length === 0) {
          // If no laser caps, finalize
          finalize();
          return;
        }
        
        let loadedCaps = 0;
        laserCaps.forEach((cap, index) => {
          const capImg = new Image();
          capImg.onload = () => {
            ctx.save();
           
            // Scale position and dimensions
            const scaledX = cap.x * canvasRatio;
            const scaledY = cap.y * canvasRatio;
            const scaledWidth = cap.width * canvasRatio;
            const scaledHeight = cap.height * canvasRatio;
           
            // Calculate center point for transformations
            const centerX = scaledX + scaledWidth / 2;
            const centerY = scaledY + scaledHeight / 2;
           
            // Apply transformations
            ctx.translate(centerX, centerY);
            // Apply rotation if defined
            if (cap.rotation) {
              ctx.rotate((cap.rotation * Math.PI) / 180);
            }
            ctx.scale(cap.flippedX ? -1 : 1, cap.flippedY ? -1 : 1);
            ctx.translate(-centerX, -centerY);
           
            // Draw the cap
            ctx.drawImage(
              capImg,
              0, 0,
              cap.originalWidth, cap.originalHeight,
              scaledX, scaledY,
              scaledWidth, scaledHeight
            );
           
            ctx.restore();
           
            loadedCaps++;
            if (loadedCaps === laserCaps.length) {
              finalize();
            }
          };
         
          capImg.onerror = () => {
            console.error(`Failed to load cap image: ${cap.src}`);
            loadedCaps++;
            if (loadedCaps === laserCaps.length) {
              finalize();
            }
          };
         
          capImg.src = cap.src;
        });
      };
     
      // Finalize and set the image
      const finalize = () => {
        // Use higher quality for the high-res version
        const imageType = isHighRes ? 'image/png' : 'image/png';
        const quality = isHighRes ? 1.0 : 0.9;
        const dataUrl = canvas.toDataURL(imageType, quality);
        
        if (isHighRes) {
          setHighResFinalImage(dataUrl);
        } else {
          setFinalImage(dataUrl);
          setShowProcessed(true);
        }
      };
     
      // Start the drawing process
      drawBackgrounds();
    };
   
    baseImg.onerror = () => {
      console.error('Failed to load base image');
    };
   
    baseImg.src = baseImage.src;
  };


  const downloadImage = () => {
    // Use high-res image if available, otherwise fall back to standard
    const imageToDownload = highResFinalImage || finalImage;
    if (!imageToDownload) return;
   
    const link = document.createElement('a');
    link.href = imageToDownload;
    link.download = 'succinct-profile.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
 
  const copyImageToClipboard = async () => {
    if (!finalImage) return;
   
    try {
      // Try the modern Clipboard API first
      const blob = await (await fetch(finalImage)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      alert('Image copied to clipboard!');
    } catch (err) {
      console.error('Clipboard write failed:', err);
     
      // For iOS Safari, offer alternative instructions
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        alert('Direct clipboard access is not supported on iOS.\n\nPlease use the Download button and save the image to your Photos, or press and hold on the image to save it manually.');
      } else {
        alert('Failed to copy image. Your browser may not support this feature.\n\nPlease use the Download button instead.');
      }
    }
  };


  // Reset the app state for a new image
  const resetForNewImage = () => {
    // Keep input ref but reset other state
    setBaseImage(null);
    setCaps([]);
    setBackgrounds([]);
    setFinalImage(null);
    setShowProcessed(false);
    setSelectedItem(null);
    setBaseImageLoaded(false);
   
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
 
  // Return to edit mode
  const returnToEdit = () => {
    setShowProcessed(false);
  };
 
  // Add this function in your component before the return statement
  const shareToTwitter = async () => {
    if (!finalImage) return;
   
    // Check if on iOS
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
   
    // Updated tweet text with improved formatting
    const tweetText = encodeURIComponent('new pfp, who dis?\n\n#Succinct #Succinctify #SuccinctPFP\n\nMake yours here: www.succinctify.app');
   
    try {
      // Try clipboard copy - will fail on iOS
      if (!isIOS) {
        const blob = await (await fetch(finalImage)).blob();
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ]);
       
        // Show instructions first, then open Twitter
        alert('Image copied to clipboard!\n\nClick in the tweet box and paste (Ctrl+V/+V) to add your image.');
        window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
      } else {
        // iOS-specific flow with multiple options
        // Give iOS-specific manual instructions with both methods
        alert('On iOS, you have two options:\n\n1. Press and hold on the profile picture � Copy � Open Twitter � Press and hold in tweet area � Paste\n\n2. Press and hold on the profile picture � Save Image � Open Twitter � Tap image icon � Select from Photos');
       
        // Open Twitter after instructions are acknowledged
        window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
      }
    } catch (err) {
      console.error('Failed to share to Twitter:', err);
     
      // Fallback to manual instructions for all platforms
      try {
        alert('Please save your profile picture manually:\n\n1. Press and hold on the image and select "Save Image" or "Copy"\n2. When Twitter opens, either paste the image or tap the image icon to select it from your Photos');
        window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
      } catch (openErr) {
        alert('There was an issue sharing to Twitter. Please try downloading your image manually and share it to Twitter yourself.');
      }
    }
  };


  // Render canvas whenever relevant state changes
  useEffect(() => {
    if (baseImage) {
      drawCanvas();
    }
  }, [baseImage, caps, backgrounds, selectedItem, baseImageLoaded, showProcessed, selectedFilter, filterStrength, layerOrder]);
 
  // Draw all canvas elements sequentially to avoid race conditions
const drawCanvas = async () => {
  const canvas = canvasRef.current;
  if (!canvas || !baseImage) return;
 
  setCanvasRendering(true);
 
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
 
  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
 
  // Fill background with theme color only if not a fill-canvas image
  if (!baseImage.fillCanvas) {
    ctx.fillStyle = '#F06FBE';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
 
  // Draw base image WITHOUT applying filter yet
  await new Promise((resolve) => {
    const baseImg = new Image();
    baseImg.onload = () => {
      // Apply different rendering based on image type
      if (baseImage.fillCanvas || baseImage.isNearlySquare) {
        // For square images, fill the entire canvas
        ctx.drawImage(baseImg, baseImage.x, baseImage.y, baseImage.width, baseImage.height);
      } else if (baseImage.isLandscape || baseImage.isPortrait) {
        // For rectangular images, draw with the calculated dimensions
        ctx.drawImage(baseImg, baseImage.x, baseImage.y, baseImage.width, baseImage.height);
        
        // Optional: add subtle aesthetic treatment for rectangular images
        if (!baseImage.fillCanvas) {
          ctx.strokeStyle = 'rgba(255,255,255,0.3)';
          ctx.lineWidth = 2;
          ctx.strokeRect(baseImage.x, baseImage.y, baseImage.width, baseImage.height);
        }
      } else {
        // Fallback for any other case
        ctx.drawImage(baseImg, baseImage.x, baseImage.y, baseImage.width, baseImage.height);
      }
      
      resolve();
    };
    baseImg.onerror = () => {
      console.error('Failed to load base image');
      resolve();
    };
    baseImg.src = baseImage.src;
  });

  // Get all layers to draw
  let allLayers = [];
  
  // Use layerOrder to determine drawing order if it has items, otherwise fallback to default order
  if (layerOrder.length > 0) {
    // Map layer IDs to actual items
    allLayers = layerOrder
      .map(id => {
        const bgItem = backgrounds.find(bg => bg.id === id);
        if (bgItem) return {...bgItem, type: 'background'};
        
        const capItem = caps.find(cap => cap.id === id);
        if (capItem) return {...capItem, type: 'cap'};
        
        return null;
      })
      .filter(item => item !== null);
  } else {
    // Fallback to default order (backgrounds first, then caps)
    allLayers = [
      ...backgrounds.map(bg => ({...bg, type: 'background'})),
      ...caps.map(cap => ({...cap, type: 'cap'}))
    ];
  }

  // Create temporary copies to update with checkmark bounds
  let updatedCaps = [...caps];
  let updatedBackgrounds = [...backgrounds];

  // Separate layers into laser and non-laser items
  const nonLaserItems = allLayers.filter(item => 
    !item.isLaser && 
    !item.src.toLowerCase().includes('laser') && 
    !(item.name && item.name.toLowerCase().includes('laser'))
  );
  
  const laserItems = allLayers.filter(item => 
    item.isLaser || 
    item.src.toLowerCase().includes('laser') || 
    (item.name && item.name.toLowerCase().includes('laser'))
  );

  // Draw non-laser items first
  for (const item of nonLaserItems) {
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.save();
        
        // Calculate center point for transformations
        const centerX = item.x + item.width / 2;
        const centerY = item.y + item.height / 2;
        
        // Apply transformations
        ctx.translate(centerX, centerY);
        if (item.rotation) {
          ctx.rotate((item.rotation * Math.PI) / 180);
        }
        ctx.scale(item.flippedX ? -1 : 1, item.flippedY ? -1 : 1);
        ctx.translate(-centerX, -centerY);
        
        // Draw the image
        ctx.drawImage(
          img,
          0, 0,
          item.originalWidth, item.originalHeight,
          item.x, item.y,
          item.width, item.height
        );
        
        // Draw selection outline if selected
        if (item.id === selectedItem) {
          ctx.setLineDash([5, 5]);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.strokeRect(item.x, item.y, item.width, item.height);
          
          ctx.restore(); // Restore before drawing the checkmark to avoid transformations
          ctx.save();
          
          // Draw checkmark button OUTSIDE the item boundary
          const btnSize = Math.min(32, Math.max(24, item.width * 0.15));
          const padding = 5; // Space between item and checkmark
          
          // Determine best position for the checkmark (8 possible positions)
          let btnX, btnY;
          const bounds = {
            left: item.x,
            right: item.x + item.width,
            top: item.y,
            bottom: item.y + item.height,
            centerX: item.x + item.width/2,
            centerY: item.y + item.height/2,
            width: item.width,
            height: item.height
          };
          
          // Canvas boundary limits
          const canvasLimits = {
            left: 10,
            top: 10,
            right: canvas.width - 10,
            bottom: canvas.height - 10
          };
          
          // Try positions in priority order (top-right, top-left, bottom-right, bottom-left, etc.)
          // Position 1: Top-right
          btnX = bounds.right + padding;
          btnY = bounds.top - btnSize - padding;
          
          // If outside canvas boundary or overlapping with the item, try next position
          if (btnX + btnSize > canvasLimits.right || btnY < canvasLimits.top || 
              (btnX < bounds.right && btnY + btnSize > bounds.top)) {
            // Position 2: Top-left
            btnX = bounds.left - btnSize - padding;
            btnY = bounds.top - btnSize - padding;
            
            if (btnX < canvasLimits.left || btnY < canvasLimits.top || 
                (btnX + btnSize > bounds.left && btnY + btnSize > bounds.top)) {
              // Position 3: Bottom-right
              btnX = bounds.right + padding;
              btnY = bounds.bottom + padding;
              
              if (btnX + btnSize > canvasLimits.right || btnY + btnSize > canvasLimits.bottom || 
                  (btnX < bounds.right && btnY < bounds.bottom)) {
                // Position 4: Bottom-left
                btnX = bounds.left - btnSize - padding;
                btnY = bounds.bottom + padding;
                
                if (btnX < canvasLimits.left || btnY + btnSize > canvasLimits.bottom || 
                    (btnX + btnSize > bounds.left && btnY < bounds.bottom)) {
                  // Position 5: Middle-right
                  btnX = bounds.right + padding;
                  btnY = bounds.centerY - btnSize/2;
                  
                  if (btnX + btnSize > canvasLimits.right || 
                      (btnX < bounds.right && btnY + btnSize > bounds.top && btnY < bounds.bottom)) {
                    // Position 6: Middle-left
                    btnX = bounds.left - btnSize - padding;
                    btnY = bounds.centerY - btnSize/2;
                    
                    if (btnX < canvasLimits.left || 
                        (btnX + btnSize > bounds.left && btnY + btnSize > bounds.top && btnY < bounds.bottom)) {
                      // Position 7: Top-middle
                      btnX = bounds.centerX - btnSize/2;
                      btnY = bounds.top - btnSize - padding;
                      
                      if (btnY < canvasLimits.top || 
                          (btnX + btnSize > bounds.left && btnX < bounds.right && btnY + btnSize > bounds.top)) {
                        // Position 8: Bottom-middle
                        btnX = bounds.centerX - btnSize/2;
                        btnY = bounds.bottom + padding;
                        
                        // Last resort: if all positions fail, use top-right corner but ensure it's within canvas
                        if (btnY + btnSize > canvasLimits.bottom || 
                            (btnX + btnSize > bounds.left && btnX < bounds.right && btnY < bounds.bottom)) {
                          btnX = Math.min(canvasLimits.right - btnSize, bounds.right + padding);
                          btnY = Math.max(canvasLimits.top, bounds.top - btnSize - padding);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          
          // Ensure the button is fully within canvas bounds
          btnX = Math.max(canvasLimits.left, Math.min(btnX, canvasLimits.right - btnSize));
          btnY = Math.max(canvasLimits.top, Math.min(btnY, canvasLimits.bottom - btnSize));
          
          // Circle background
          ctx.fillStyle = 'rgba(46, 204, 113, 0.95)'; // Green with high opacity
          ctx.beginPath();
          ctx.arc(btnX + btnSize/2, btnY + btnSize/2, btnSize/2, 0, Math.PI * 2);
          ctx.fill();
          
          // Add a white outline
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(btnX + btnSize/2, btnY + btnSize/2, btnSize/2 + 1, 0, Math.PI * 2);
          ctx.stroke();
          
          // Checkmark
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(btnX + btnSize * 0.25, btnY + btnSize * 0.5);
          ctx.lineTo(btnX + btnSize * 0.45, btnY + btnSize * 0.7);
          ctx.lineTo(btnX + btnSize * 0.75, btnY + btnSize * 0.3);
          ctx.stroke();
          
          // Store the checkmark bounds
          if (item.type === 'background') {
            const bgIndex = updatedBackgrounds.findIndex(bg => bg.id === item.id);
            if (bgIndex !== -1) {
              updatedBackgrounds[bgIndex] = {
                ...updatedBackgrounds[bgIndex],
                checkmarkBounds: { x: btnX, y: btnY, width: btnSize, height: btnSize }
              };
            }
          } else if (item.type === 'cap') {
            const capIndex = updatedCaps.findIndex(cap => cap.id === item.id);
            if (capIndex !== -1) {
              updatedCaps[capIndex] = {
                ...updatedCaps[capIndex],
                checkmarkBounds: { x: btnX, y: btnY, width: btnSize, height: btnSize }
              };
            }
          }
        }
        
        ctx.restore();
        resolve();
      };
      
      img.onerror = () => {
        console.error(`Failed to load image: ${item.src}`);
        resolve();
      };
      
      img.src = item.src;
    });
  }

  // Apply filter after drawing non-laser items but before laser items
  if (selectedFilter) {
    const selectedFilterObj = colorFilters.find(filter => filter.id === selectedFilter);
    if (selectedFilterObj) {
      // Get the image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Apply the filter with the specified strength
      const strength = filterStrength / 100; // Convert percentage to decimal
      const [r, g, b] = selectedFilterObj.rgb;
      
      for (let i = 0; i < data.length; i += 4) {
        // Skip fully transparent pixels
        if (data[i + 3] === 0) continue;
        
        // Apply the filter based on strength
        data[i] = Math.round(data[i] * (1 - strength) + r * strength);
        data[i + 1] = Math.round(data[i + 1] * (1 - strength) + g * strength);
        data[i + 2] = Math.round(data[i + 2] * (1 - strength) + b * strength);
      }
      
      // Put the modified image data back
      ctx.putImageData(imageData, 0, 0);
    }
  }

  // Draw laser items after filter
  for (const item of laserItems) {
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.save();
        
        // Calculate center point for transformations
        const centerX = item.x + item.width / 2;
        const centerY = item.y + item.height / 2;
        
        // Apply transformations
        ctx.translate(centerX, centerY);
        if (item.rotation) {
          ctx.rotate((item.rotation * Math.PI) / 180);
        }
        ctx.scale(item.flippedX ? -1 : 1, item.flippedY ? -1 : 1);
        ctx.translate(-centerX, -centerY);
        
        // Draw the image
        ctx.drawImage(
          img,
          0, 0,
          item.originalWidth, item.originalHeight,
          item.x, item.y,
          item.width, item.height
        );
        
        // Draw selection outline if selected
        if (item.id === selectedItem) {
          ctx.setLineDash([5, 5]);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.strokeRect(item.x, item.y, item.width, item.height);
          
          ctx.restore(); // Restore before drawing the checkmark
          ctx.save();
          
          // Apply the same outside checkmark logic as for non-laser items
          const btnSize = Math.min(32, Math.max(24, item.width * 0.15));
          const padding = 5;
          
          // Start with top-right position
          let btnX = item.x + item.width + padding;
          let btnY = item.y - btnSize - padding;
          
          // Canvas boundary limits
          const canvasLimits = {
            left: 10,
            top: 10, 
            right: canvas.width - 10,
            bottom: canvas.height - 10
          };
          
          // Use the same positioning algorithm as before
          const bounds = {
            left: item.x,
            right: item.x + item.width,
            top: item.y,
            bottom: item.y + item.height,
            centerX: item.x + item.width/2,
            centerY: item.y + item.height/2,
            width: item.width,
            height: item.height
          };
          
          // Try positions in order (same as before)
          // Position 1: Top-right
          btnX = bounds.right + padding;
          btnY = bounds.top - btnSize - padding;
          
          if (btnX + btnSize > canvasLimits.right || btnY < canvasLimits.top || 
              (btnX < bounds.right && btnY + btnSize > bounds.top)) {
            // Position 2: Top-left
            btnX = bounds.left - btnSize - padding;
            btnY = bounds.top - btnSize - padding;
            
            // Continue with all positioning checks as before...
            if (btnX < canvasLimits.left || btnY < canvasLimits.top || 
                (btnX + btnSize > bounds.left && btnY + btnSize > bounds.top)) {
              // Position 3: Bottom-right
              btnX = bounds.right + padding;
              btnY = bounds.bottom + padding;
              
              if (btnX + btnSize > canvasLimits.right || btnY + btnSize > canvasLimits.bottom || 
                  (btnX < bounds.right && btnY < bounds.bottom)) {
                // Position 4: Bottom-left
                btnX = bounds.left - btnSize - padding;
                btnY = bounds.bottom + padding;
                
                if (btnX < canvasLimits.left || btnY + btnSize > canvasLimits.bottom || 
                    (btnX + btnSize > bounds.left && btnY < bounds.bottom)) {
                  // Position 5: Middle-right
                  btnX = bounds.right + padding;
                  btnY = bounds.centerY - btnSize/2;
                  
                  if (btnX + btnSize > canvasLimits.right || 
                      (btnX < bounds.right && btnY + btnSize > bounds.top && btnY < bounds.bottom)) {
                    // Position 6: Middle-left
                    btnX = bounds.left - btnSize - padding;
                    btnY = bounds.centerY - btnSize/2;
                    
                    if (btnX < canvasLimits.left || 
                        (btnX + btnSize > bounds.left && btnY + btnSize > bounds.top && btnY < bounds.bottom)) {
                      // Position 7: Top-middle
                      btnX = bounds.centerX - btnSize/2;
                      btnY = bounds.top - btnSize - padding;
                      
                      if (btnY < canvasLimits.top || 
                          (btnX + btnSize > bounds.left && btnX < bounds.right && btnY + btnSize > bounds.top)) {
                        // Position 8: Bottom-middle
                        btnX = bounds.centerX - btnSize/2;
                        btnY = bounds.bottom + padding;
                        
                        if (btnY + btnSize > canvasLimits.bottom || 
                            (btnX + btnSize > bounds.left && btnX < bounds.right && btnY < bounds.bottom)) {
                          btnX = Math.min(canvasLimits.right - btnSize, bounds.right + padding);
                          btnY = Math.max(canvasLimits.top, bounds.top - btnSize - padding);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          
          // Ensure button stays within canvas bounds
          btnX = Math.max(canvasLimits.left, Math.min(btnX, canvasLimits.right - btnSize));
          btnY = Math.max(canvasLimits.top, Math.min(btnY, canvasLimits.bottom - btnSize));
          
          // Draw the checkmark button
          ctx.fillStyle = 'rgba(46, 204, 113, 0.95)';
          ctx.beginPath();
          ctx.arc(btnX + btnSize/2, btnY + btnSize/2, btnSize/2, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(btnX + btnSize/2, btnY + btnSize/2, btnSize/2 + 1, 0, Math.PI * 2);
          ctx.stroke();
          
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(btnX + btnSize * 0.25, btnY + btnSize * 0.5);
          ctx.lineTo(btnX + btnSize * 0.45, btnY + btnSize * 0.7);
          ctx.lineTo(btnX + btnSize * 0.75, btnY + btnSize * 0.3);
          ctx.stroke();
          
          // Store the checkmark bounds
          if (item.type === 'background') {
            const bgIndex = updatedBackgrounds.findIndex(bg => bg.id === item.id);
            if (bgIndex !== -1) {
              updatedBackgrounds[bgIndex] = {
                ...updatedBackgrounds[bgIndex],
                checkmarkBounds: { x: btnX, y: btnY, width: btnSize, height: btnSize }
              };
            }
          } else if (item.type === 'cap') {
            const capIndex = updatedCaps.findIndex(cap => cap.id === item.id);
            if (capIndex !== -1) {
              updatedCaps[capIndex] = {
                ...updatedCaps[capIndex],
                checkmarkBounds: { x: btnX, y: btnY, width: btnSize, height: btnSize }
              };
            }
          }
        }
        
        ctx.restore();
        resolve();
      };
      
      img.onerror = () => {
        console.error(`Failed to load image: ${item.src}`);
        resolve();
      };
      
      img.src = item.src;
    });
  }

  // Update the caps and backgrounds state with the new checkmarkBounds
  if (JSON.stringify(backgrounds) !== JSON.stringify(updatedBackgrounds)) {
    setBackgrounds(updatedBackgrounds);
  }
  
  if (JSON.stringify(caps) !== JSON.stringify(updatedCaps)) {
    setCaps(updatedCaps);
  }

  setCanvasRendering(false);
};


// Function to handle filter selection
const applyColorFilter = (filterId) => {
  // If clicking the same filter, toggle it off
  if (selectedFilter === filterId) {
    setSelectedFilter(null);
  } else {
    setSelectedFilter(filterId);
  }
};


  // Add the Twitter Profile Fetch Function
  const fetchTwitterProfile = async () => {
    if (!twitterUsername) {
      setTwitterProfileError('Please enter a Twitter username');
      return;
    }
    
    setTwitterProfileError(null);
    setIsLoadingTwitterProfile(true);
    
    try {
      // Remove @ if present and trim whitespace
      const username = twitterUsername.replace('@', '').trim();
      
      // Updated URL to use the correct service endpoint for X (formerly Twitter)
      const imageUrl = `https://unavatar.io/x/${username}`;
      
      // Create a promise that resolves when the image loads or rejects when it fails
      const imageLoadPromise = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.crossOrigin = "anonymous"; // Attempt to avoid CORS issues
        img.src = imageUrl;
      });
      
      // Wait for the image to load (with timeout)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000) // Longer timeout
      );
      
      const img = await Promise.race([imageLoadPromise, timeoutPromise]);
      
      // If the image is a 1x1 pixel, it's likely the default avatar (failed to get real image)
      if (img.width === 1 && img.height === 1) {
        throw new Error('Invalid profile image returned');
      }
      
      // If we got here, the image loaded successfully
      // Create a canvas to handle potential CORS issues
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      // Try to get data URL from canvas - if this fails, it's likely due to CORS
      let dataUrl;
      try {
        dataUrl = canvas.toDataURL('image/png');
      } catch (corsError) {
        console.error('CORS error when converting image:', corsError);
        throw new Error('Could not load the Twitter image due to security restrictions');
      }
      
      // Canvas size is fixed at 800x800
      const canvasSize = 800;
      
      // Original dimensions
      const originalWidth = img.width;
      const originalHeight = img.height;
      
      // Calculate scaled dimensions to fit within canvas - always treat as square
      // Twitter profile pics are typically square already
      const newBaseImage = {
        src: dataUrl,
        x: 0,
        y: 0,
        width: canvasSize,
        height: canvasSize,
        originalWidth: img.width,
        originalHeight: img.height,
        needsUpscaling: originalWidth < 400 || originalHeight < 400,
        fillCanvas: true,
        isLandscape: false,
        isPortrait: false
      };
      
      setBaseImage(newBaseImage);
      setBaseImageLoaded(true);
      setIsLoadingTwitterProfile(false);
      setTwitterUsername('');
    } catch (error) {
      console.error('Error importing Twitter profile:', error);
      setTwitterProfileError('Could not load Twitter profile image. Try another username or upload manually.');
      setIsLoadingTwitterProfile(false);
    }
  };


  // Add function to handle layer drag start
  const handleLayerDragStart = (e, id) => {
    setIsDraggingLayer(true);
    setDraggedLayerId(id);
    // Add visual feedback
    e.currentTarget.classList.add('opacity-50');
  };

  // Add function to handle layer drag over
  const handleLayerDragOver = (e, id) => {
    e.preventDefault();
    setDraggedOverLayerId(id);
  };

  // Add function to handle layer drop
  const handleLayerDrop = (e) => {
    e.preventDefault();
    
    if (draggedLayerId && draggedOverLayerId && draggedLayerId !== draggedOverLayerId) {
      // Create new array with reordered items
      const newLayerOrder = [...layerOrder];
      const draggedItemIndex = newLayerOrder.indexOf(draggedLayerId);
      const dropTargetIndex = newLayerOrder.indexOf(draggedOverLayerId);
      
      // Remove the item from its original position
      newLayerOrder.splice(draggedItemIndex, 1);
      
      // Insert it at the new position
      newLayerOrder.splice(dropTargetIndex, 0, draggedLayerId);
      
      // Ensure laser items stay on top
      const updatedOrder = [];
      
      // Add all laser items first
      newLayerOrder.forEach(id => {
        const item = [...caps, ...backgrounds].find(item => item.id === id);
        if (item && (item.isLaser || item.src.toLowerCase().includes('laser') || 
            (item.name && item.name.toLowerCase().includes('laser')))) {
          updatedOrder.push(id);
        }
      });
      
      // Then add all non-laser items
      newLayerOrder.forEach(id => {
        const item = [...caps, ...backgrounds].find(item => item.id === id);
        if (item && !item.isLaser && !item.src.toLowerCase().includes('laser') && 
            !(item.name && item.name.toLowerCase().includes('laser'))) {
          updatedOrder.push(id);
        }
      });
      
      setLayerOrder(updatedOrder);
    }
    
    // Reset states
    setIsDraggingLayer(false);
    setDraggedLayerId(null);
    setDraggedOverLayerId(null);
    
    // Remove visual feedback from all items
    document.querySelectorAll('.layer-item').forEach(item => {
      item.classList.remove('opacity-50');
    });
  };

  // Add function to handle layer drag end
  const handleLayerDragEnd = (e) => {
    // Remove visual feedback
    e.currentTarget.classList.remove('opacity-50');
    
    // Reset states
    setIsDraggingLayer(false);
    setDraggedLayerId(null);
    setDraggedOverLayerId(null);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-pink-400 to-purple-600 text-white">
      <header className="p-4 flex items-center justify-center border-b border-pink-300">
        <img
          src="/images/succinct_logo.png"
          alt="Succinct Logo"
          style={{ width: '320px', height: '80px' }}
        />
      </header>
     
      <main className="flex flex-col flex-grow p-4 overflow-hidden">
        <div className="flex flex-col md:flex-row gap-4 h-full">
          {/* Main Canvas Area - Full width when no image is selected */}
          <div className={`flex-grow flex flex-col items-center justify-center bg-pink-800 bg-opacity-30 rounded-lg p-4 ${!baseImage ? 'mx-auto md:w-3/4 lg:w-2/3' : ''}`}>
            {!baseImage ? (
              <div className="text-center p-8 flex flex-col justify-center items-center h-full min-h-[400px] md:min-h-[600px]">
                <div className="flex-grow flex flex-col justify-center items-center">
                  <div className="mx-auto h-32 w-32 mb-8 flex items-center justify-center">
                    <Upload size={96} />
                  </div>
                  <h2 className="text-3xl font-bold mb-4">Succinctify Your Profile Picture</h2>
                  <p className="mb-8 text-xl max-w-lg">
                    Upload an image and customize it with fun caps and stickers.
                  </p>
                  <button
                    className="bg-pink-600 hover:bg-pink-700 py-4 px-8 rounded-lg flex items-center mx-auto text-xl font-medium shadow-lg transform transition hover:scale-105"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-3 h-7 w-7" />
                    Choose Image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleBaseImageUpload}
                  />
                  
                  {/* Add Twitter Import Feature Here */}
                  <div className="mt-8 relative w-full max-w-md">
                    <div className="absolute inset-0 flex items-center">
                      <div className="border-t border-pink-300 w-full"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gradient-to-br from-pink-400 to-purple-600 text-white font-medium">
                        OR IMPORT FROM TWITTER
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 w-full max-w-md">
                    <div className="flex">
                      <div className="flex-grow">
                        <div className="relative flex items-center">
                          <div className="absolute left-3 text-pink-400 pointer-events-none">
                            @
                          </div>
                          <input
                            type="text"
                            placeholder="username"
                            className="w-full py-3 pl-8 pr-4 bg-purple-800 bg-opacity-50 text-white rounded-l-lg outline-none border border-purple-500 focus:border-pink-400 placeholder-pink-300"
                            value={twitterUsername}
                            onChange={(e) => setTwitterUsername(e.target.value)}
                            disabled={isLoadingTwitterProfile}
                          />
                        </div>
                      </div>
                      <button
                        className={`py-3 px-5 rounded-r-lg flex items-center justify-center text-lg font-medium shadow-lg transform transition hover:scale-105 ${
                          isLoadingTwitterProfile 
                            ? 'bg-purple-800 cursor-not-allowed' 
                            : 'bg-pink-600 hover:bg-pink-700'
                        }`}
                        onClick={fetchTwitterProfile}
                        disabled={isLoadingTwitterProfile}
                      >
                        {isLoadingTwitterProfile ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                        ) : (
                          <>
                            <FaXTwitter className="mr-2 h-5 w-5" />
                            Import
                          </>
                        )}
                      </button>
                    </div>
                    
                    {twitterProfileError && (
                      <div className="mt-2 text-red-300 text-sm text-center">
                        {twitterProfileError}
                      </div>
                    )}
                  </div>
                </div>
               
                <div className="mt-auto pt-8">
                  <p className="text-pink-200 text-sm">
                    Supported formats: JPG, PNG, GIF " Max size: 10MB
                  </p>
                </div>
              </div>
            ) : showProcessed ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] md:min-h-[600px] w-full max-w-2xl mx-auto py-10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-4">Your Profile Picture</h2>
                  <p className="text-xl text-pink-100 mb-6">Your profile picture is ready! Download it or copy to clipboard.</p>
                </div>
               
                <img
                  src={finalImage}
                  alt="Processed profile"
                  className="w-64 h-64 md:w-80 md:h-80 rounded-full border-4 border-white shadow-xl mb-8"
                />
               
                <div className="flex flex-wrap gap-4 justify-center">
                  <button
                    className="bg-pink-600 hover:bg-pink-700 py-3 px-6 rounded-lg flex items-center font-medium text-lg shadow-lg transform transition hover:scale-105"
                    onClick={downloadImage}
                  >
                    <Download className="mr-3 h-6 w-6" />
                    Download HD Image
                  </button>
                  <button
                    className="bg-pink-600 hover:bg-pink-700 py-3 px-6 rounded-lg flex items-center font-medium text-lg shadow-lg transform transition hover:scale-105"
                    onClick={copyImageToClipboard}
                  >
                    <Layers className="mr-3 h-6 w-6" />
                    Copy to Clipboard
                  </button>
                  <button
                    className="bg-purple-600 hover:bg-purple-700 py-3 px-6 rounded-lg flex items-center font-medium text-lg shadow-lg transform transition hover:scale-105"
                    onClick={returnToEdit}
                  >
                    <RotateCcw className="mr-3 h-6 w-6" />
                    Edit More
                  </button>
                </div>
               
                <div className="mt-10 text-center">
                  <p className="text-pink-200 mb-4">
                    Share your creation with <span className="font-bold">#SuccinctPFP</span> on social media!
                  </p>
                  <button
                    className="bg-blue-500 hover:bg-blue-600 py-3 px-6 rounded-lg flex items-center font-medium mx-auto shadow-lg transform transition hover:scale-105"
                    onClick={shareToTwitter}
                  >
                    <FaXTwitter className="mr-3 h-6 w-6" />
                    {/iPhone|iPad|iPod/.test(navigator.userAgent) ?
                      'Tweet with Image (Copy or Save)' :
                      'Tweet with Image (Auto-copied)'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile-only tip above the canvas */}
                <div className="w-full mb-2 bg-black bg-opacity-40 text-white text-xs rounded p-2 text-center md:hidden">
                  <span>
                    Tip: Click and drag items to move them. Click anywhere in the image to exit control menu and show stickers.
                  </span>
                </div>


                <div className="relative">
                  {/* Desktop-only tip - positioned in top-left of canvas */}
                  <div className="absolute top-3 left-3 z-10 bg-black bg-opacity-70 text-white text-sm rounded-md px-3 py-2 hidden md:block max-w-[200px]">
                    <span>Tip: Click and drag items to move them</span>
                  </div>
                  
                  {/* Show a message if image needed upscaling */}
                  {baseImage?.needsUpscaling && (
                    <div className="absolute top-3 right-3 z-10 bg-yellow-600 bg-opacity-90 text-white text-sm rounded-md px-3 py-2 max-w-[250px]">
                      <span>Notice: This image was upscaled to improve quality</span>
                    </div>
                  )}


                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={800}
                    className="max-w-full max-h-full border-2 border-white rounded-lg shadow-xl object-contain cursor-move"
                  />
                  {canvasRendering && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent"></div>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  <button
                    className="bg-green-600 hover:bg-green-700 py-2 px-6 rounded-full flex items-center font-bold"
                    onClick={processImage}
                    disabled={canvasRendering}
                  >
                    <Check className="mr-2 h-5 w-5" />
                    Process Image
                  </button>
                </div>
              </>
            )}
          </div>
         
          {/* Only show sidebar when an image is uploaded and not in processed view */}
          {baseImage && !showProcessed && (
            <div className="w-full md:w-80 flex flex-col bg-purple-800 bg-opacity-30 rounded-lg overflow-hidden h-full max-h-[calc(100vh-200px)]">
              {/* Controls Section - Auto height to fit content */}
              <div className="p-4 border-b border-pink-300 overflow-y-auto" style={{ flex: '0 0 auto' }}>
                <h2 className="text-lg font-semibold mb-2 sticky top-0 bg-purple-800 bg-opacity-90 py-1 -mt-1 -mx-1 px-1">Controls</h2>
               
                {selectedItem ? (
                  <>
                    {/* Position controls removed as dragging works well */}
                    
                    <div className="mb-4">
                      <h3 className="text-sm font-medium mb-1">Size</h3>
                      <div className="flex space-x-2">
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                          onClick={() => resizeItem(0.9)}
                        >
                          <Minimize2 className="h-5 w-5" />
                        </button>
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                          onClick={() => resizeItem(1.1)}
                        >
                          <Maximize2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="text-sm font-medium mb-1">Stretch</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col space-y-1">
                          <p className="text-xs text-center">Width</p>
                          <div className="flex space-x-1">
                            <button
                              className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                              onClick={() => stretchItemWidth(0.9)}
                              title="Narrow"
                            >
                              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="5" y="4" width="14" height="16" rx="1" />
                                <path d="M12 8L9 12L12 16M12 8L15 12L12 16" />
                              </svg>
                            </button>
                            <button
                              className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                              onClick={() => stretchItemWidth(1.1)}
                              title="Widen"
                            >
                              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="5" y="4" width="14" height="16" rx="1" />
                                <path d="M9 8L12 12L9 16M15 8L12 12L15 16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-1">
                          <p className="text-xs text-center">Height</p>
                          <div className="flex space-x-1">
                            <button
                              className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                              onClick={() => stretchItemHeight(0.9)}
                              title="Shorten"
                            >
                              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="4" y="5" width="16" height="14" rx="1" />
                                <path d="M8 12L12 9L16 12M8 12L12 15L16 12" />
                              </svg>
                            </button>
                            <button
                              className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                              onClick={() => stretchItemHeight(1.1)}
                              title="Lengthen"
                            >
                              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="4" y="5" width="16" height="14" rx="1" />
                                <path d="M8 9L12 12L8 15M16 9L12 12L16 15" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="text-sm font-medium mb-1">Rotation</h3>
                      <div className="flex space-x-2">
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                          onClick={() => rotateItem(-15)}
                          title="Rotate counterclockwise"
                        >
                          <RotateCcw className="h-5 w-5" />
                        </button>
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded flex items-center justify-center"
                          onClick={() => rotateItem(15)}
                          title="Rotate clockwise"
                        >
                          <RotateCw className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="text-sm font-medium mb-1">Flip</h3>
                      <div className="flex space-x-2">
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded"
                          onClick={() => flipItem('x')}
                        >
                          Flip H
                        </button>
                        <button
                          className="flex-1 bg-purple-700 hover:bg-purple-800 p-2 rounded"
                          onClick={() => flipItem('y')}
                        >
                          Flip V
                        </button>
                      </div>
                    </div>
                    
                    <button
                      className="w-full bg-red-600 hover:bg-red-700 py-2 px-4 rounded flex items-center justify-center"
                      onClick={removeItem}
                    >
                      <X className="mr-2 h-5 w-5" />
                      Remove
                    </button>
                  </>
                ) : (
                  <p className="text-pink-200">Select an item to edit it</p>
                )}
              </div>
             
              {/* All Items Section - Tabbed Interface */}
              <div className="flex flex-col overflow-hidden" style={{ flex: '1 1 30%' }}>
                {/* Tab Navigation */}
                <div className="flex border-b border-pink-300">
                  <button
                    className={`flex-1 py-3 font-semibold text-center transition-colors ${
                      activeTab === 'stickers' ? 'bg-pink-600 text-white' : 'bg-purple-800 bg-opacity-30 hover:bg-purple-700'
                    }`}
                    onClick={() => setActiveTab('stickers')}
                  >
                    Stickers
                  </button>
                  <button
                    className={`flex-1 py-3 font-semibold text-center transition-colors ${
                      activeTab === 'filters' ? 'bg-pink-600 text-white' : 'bg-purple-800 bg-opacity-30 hover:bg-purple-700'
                    }`}
                    onClick={() => setActiveTab('filters')}
                  >
                    Filters
                  </button>
                </div>
                
                {/* Tab Content */}
                {activeTab === 'stickers' ? (
                  /* Stickers Content */
                  <div className="overflow-y-auto p-2 flex-grow">
                    {isLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      </div>
                    ) : allItems.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-pink-200 text-sm">No items found. Add images to your assets folder.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {allItems.map(item => (
                          <button
                            key={item.id}
                            className="bg-pink-700 hover:bg-pink-800 p-2 rounded aspect-square relative"
                            onClick={() => addItem(item)}
                          >
                            <img
                              src={item.src}
                              alt={item.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/images/placeholder.png';
                              }}
                            />
                            <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-xs p-1 truncate">
                              {item.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Filters Content */
                  <div className="overflow-y-auto p-4 flex-grow">
                    <h3 className="text-sm font-medium mb-3">Color Filters</h3>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {colorFilters.map(filter => (
                        <button
                          key={filter.id}
                          className={`p-3 rounded flex flex-col items-center transition-all ${
                            selectedFilter === filter.id 
                              ? 'bg-pink-600 ring-2 ring-white transform scale-105' 
                              : 'bg-purple-700 hover:bg-purple-600'
                          }`}
                          onClick={() => applyColorFilter(filter.id)}
                        >
                          <div 
                            className="w-12 h-12 rounded-full mb-2"
                            style={{ backgroundColor: filter.color }}
                          ></div>
                          <span className="text-xs font-medium">{filter.name}</span>
                        </button>
                      ))}
                    </div>
                    
                    {selectedFilter && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium mb-2">Filter Strength</h3>
                        <div className="flex items-center gap-2">
                          <Sliders className="h-5 w-5 text-pink-300" />
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={filterStrength}
                            onChange={(e) => setFilterStrength(parseInt(e.target.value))}
                            className="w-full h-2 bg-purple-700 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="text-sm font-bold">{filterStrength}%</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
             
              {/* Layer Manager - Fixed height with drag support */}
              <div className="border-t border-pink-300 bg-purple-800 bg-opacity-50" style={{ flex: '0 0 auto', height: 'auto', maxHeight: '150px' }}>
                <h3 className="text-sm font-semibold p-2 sticky top-0 bg-purple-800">Layers</h3>
                <div className="p-2 pt-0 overflow-y-auto" style={{ maxHeight: '110px' }}>
                  {caps.length === 0 && backgrounds.length === 0 ? (
                    <p className="text-pink-200 text-xs">No layers added yet</p>
                  ) : (
                    <>
                      {/* Sort items using layerOrder */}
                      {layerOrder.map(id => {
                        // Find the corresponding item
                        const item = [...caps, ...backgrounds].find(item => item.id === id);
                        if (!item) return null;
                        
                        // Generate a unique color based on the item's ID
                        const colorId = parseInt(item.id.split('_')[1] || '0', 10) % 12;
                        const colors = [
                          'bg-pink-300', 'bg-purple-300', 'bg-blue-300',
                          'bg-green-300', 'bg-yellow-300', 'bg-red-300',
                          'bg-indigo-300', 'bg-teal-300', 'bg-orange-300',
                          'bg-cyan-300', 'bg-lime-300', 'bg-amber-300'
                        ];
                        const color = colors[colorId] || 'bg-gray-300';
                        
                        // Extract item name
                        const fileName = item.name || item.src.split('/').pop()?.split('.')[0] || `Item`;
                        const decodedFileName = decodeURIComponent(fileName);
                        const displayName = decodedFileName.replace(/_/g, ' ').replace(/-/g, ' ');
                        
                        // Check if this is a laser item
                        const isLaserItem = item.isLaser || 
                                           item.src.toLowerCase().includes('laser') || 
                                           (item.name && item.name.toLowerCase().includes('laser'));
                          
                        return (
                          <div
                            key={item.id}
                            draggable={true}
                            onDragStart={(e) => handleLayerDragStart(e, item.id)}
                            onDragOver={(e) => handleLayerDragOver(e, item.id)}
                            onDrop={handleLayerDrop}
                            onDragEnd={handleLayerDragEnd}
                            className={`layer-item p-2 rounded flex items-center cursor-pointer mb-1 group ${
                              draggedOverLayerId === item.id ? 'border border-white' : ''
                            } ${
                              selectedItem === item.id ? 'bg-pink-600' : 'bg-purple-700 hover:bg-purple-800'
                            }`}
                            onClick={() => setSelectedItem(item.id)}
                          >
                            {/* Drag handle */}
                            <div 
                              className="mr-1 cursor-grab touch-none active:cursor-grabbing" 
                              title="Drag to reorder"
                            >
                              <svg className="w-4 h-4 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M8 6h8M8 12h8M8 18h8" strokeLinecap="round" />
                              </svg>
                            </div>
                            
                            <div className={`w-6 h-6 ${color} rounded-full mr-2`}></div>
                            <span className="text-xs truncate flex-grow">{displayName}</span>
                            <span className="text-xs text-pink-200 ml-1 flex items-center">
                              {item.id.startsWith('cap_') ? 'Cap' : 'BG'}
                              {isLaserItem && <span className="ml-1">⚡</span>}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
             
              {/* Upload Button - Always at bottom */}
              <div className="p-2 border-t border-pink-300 mt-auto">
                <button
                  className="w-full bg-pink-600 hover:bg-pink-700 py-2 px-4 rounded flex items-center justify-center"
                  onClick={resetForNewImage}
                >
                  <Upload className="mr-2 h-5 w-5" />
                  Change Image
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
     
      <footer className="p-3 border-t border-pink-300">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            Succinct Profile Picture Customizer | Made with ❤️ by
            <a
              href="https://x.com/simplyorange12"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-200 transition-colors ml-1"
            >
              @simplyorange12
            </a>
          </div>
          <div className="flex space-x-4">
            <a
              href="https://x.com/SuccinctLabs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-pink-600 hover:bg-pink-700 rounded-full transition-all"
            >
              <FaXTwitter className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://discord.gg/succinctlabs"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-pink-600 hover:bg-pink-700 rounded-full transition-all"
            >
              <FaDiscord className="w-5 h-5 text-white" />
            </a>
            <a
              href="https://www.succinct.xyz/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-pink-600 hover:bg-pink-700 rounded-full transition-all"
            >
              <Globe className="w-5 h-5 text-white" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};


export default ProfileCustomizer;















